import { Panel, SectionLabel } from '@whinerp/ui';
import { useSession } from '../../app/session.js';
import { useT } from '../../app/i18n.js';

/**
 * What this deployment can actually reach (part 3).
 *
 * Every provider, with its mode and what to do about it. Read from the session
 * rather than fetched, because the shell already has it - and because a second
 * request would be a second answer to "which mode are we in", and the two would
 * disagree on the screen that matters.
 *
 * Three modes shown as three things:
 *   live            a real provider call
 *   sandbox         composed, validated and kept here. A working mode.
 *   not-configured  no credentials. Nothing is sent and nothing pretends it was.
 */
export function ProvidersPanel(): JSX.Element {
  const t = useT();
  const { session } = useSession();
  const providers = session.providers ?? [];

  return (
    <Panel data-region="providers" id="providers">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <SectionLabel>{t('provider.title')}</SectionLabel>
          <p className="text-meta text-ink-meta">{t('provider.body')}</p>
        </div>

        <ul className="flex flex-col gap-1">
          {providers.map((provider) => (
            <li
              key={provider.key}
              data-provider={provider.key}
              data-provider-mode={provider.mode}
              className="flex flex-col gap-0.5 border-t border-t-rule pt-1 first:border-t-0 first:pt-0"
            >
              <span className="flex flex-wrap items-baseline gap-2">
                <span className="text-dense text-ink">{provider.label}</span>
                {/* The mode as a word, never as a colour alone: a dot that
                    means sandbox says nothing on a printed page and nothing to
                    somebody who cannot see the colour. */}
                <span
                  className={
                    provider.mode === 'live'
                      ? 'text-meta text-bronze-text'
                      : provider.mode === 'sandbox'
                        ? 'text-meta text-ink-600'
                        : 'text-meta text-danger-text'
                  }
                >
                  {provider.mode === 'live'
                    ? t('provider.mode.live')
                    : provider.mode === 'sandbox'
                      ? t('provider.sandbox')
                      : t('provider.notConfigured')}
                </span>
              </span>
              {provider.nextStep ? (
                <span data-provider-next-step={provider.key} className="text-meta text-ink-meta">
                  {provider.nextStep}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}
